import { MAX_CHAT_MENTIONS, validateChatMentions, type ChatMention, type MentionableChat } from '../shared/chatMentions';
import { matchesChatTitle } from '../utils/chatTitleSearch';

export interface MentionQuery { start: number; end: number; search: string }
export interface ComposerSelection { start: number; end: number; inputType?: string }

/** The attachment action uses the same literal trigger as typing, at the saved caret. */
export function startChatMention(text: string, mentions: ChatMention[], selection: ComposerSelection) {
  const start = Math.max(0, Math.min(selection.start, text.length));
  const end = Math.max(start, Math.min(selection.end, text.length));
  const activeQuery = start === end ? findMentionQuery(text, start, mentions) : null;
  const from = activeQuery?.start ?? start;
  const trigger = `${from > 0 && !/[\s([{]/.test(text[from - 1]) ? ' ' : ''}@`;
  const next = text.slice(0, from) + trigger + text.slice(end);
  return { text: next, mentions: reconcileMentionEdit(text, next, mentions, { start: from, end }), caret: from + trigger.length };
}

export function excludeCurrentChatMention(mentions: ChatMention[], currentChatId?: string | null) {
  return mentions.filter((mention) => mention.chatId !== currentChatId);
}

export function normalizeMentionableChat(chat: MentionableChat): MentionableChat {
  // Historical titles can contain C0 controls; only the mention projection is sanitized.
  // eslint-disable-next-line no-control-regex
  const title = chat.title.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim()
    .slice(0, 140).replace(/[\uD800-\uDBFF]$/, '').trimEnd();
  return { id: chat.id, title };
}

export function findMentionQuery(text: string, caret: number, mentions: ChatMention[]): MentionQuery | null {
  if (caret < 0 || caret > text.length) return null;
  if (mentions.some((mention) => caret > mention.start && caret <= mention.end)) return null;
  const start = text.lastIndexOf('@', caret - 1);
  if (start < 0 || (start > 0 && !/[\s([{]/.test(text[start - 1]))) return null;
  if (mentions.some((mention) => mention.start === start)) return null;
  const search = text.slice(start + 1, caret);
  if (/[\n\r@]/.test(search) || search.length > 100) return null;
  return { start, end: caret, search };
}

export function getMentionSuggestions(chats: MentionableChat[], query: MentionQuery | null, mentions: ChatMention[], currentChatId?: string | null, limit = 5) {
  if (!query || mentions.length >= MAX_CHAT_MENTIONS) return [];
  return chats.map(normalizeMentionableChat).filter((chat) => chat.title && chat.id !== currentChatId && !mentions.some((mention) => mention.chatId === chat.id)
    && matchesChatTitle(chat.title, query.search)).slice(0, limit);
}

/** Rename the display/draft projection by ID; never rewrite a sent Firestore message. */
export function refreshChatMentionTitles(text: string, mentions: ChatMention[], chats: MentionableChat[]) {
  const valid = validateChatMentions(mentions, text) ?? [];
  const titles = new Map(chats.map(normalizeMentionableChat).filter(chat => chat.title).map(chat => [chat.id, chat.title]));
  let result = '';
  let offset = 0;
  const refreshed: ChatMention[] = [];
  for (const mention of valid) {
    result += text.slice(offset, mention.start);
    const title = titles.get(mention.chatId) ?? mention.title;
    const start = result.length;
    result += `@${title}`;
    refreshed.push({ ...mention, title, start, end: result.length });
    offset = mention.end;
  }
  result += text.slice(offset);
  const mapOffset = (position: number) => {
    let delta = 0;
    for (let index = 0; index < valid.length; index++) {
      const previous = valid[index];
      const next = refreshed[index];
      if (position <= previous.start) break;
      if (position < previous.end) return next.start + Math.min(position - previous.start, next.end - next.start);
      delta += (next.end - next.start) - (previous.end - previous.start);
    }
    return position + delta;
  };
  return { text: result, mentions: refreshed, mapOffset };
}

/** Native textarea offsets and the persisted contract both use UTF-16 indices. */
export function reconcileMentionEdit(previous: string, next: string, mentions: ChatMention[], selection?: ComposerSelection): ChatMention[] {
  if (previous === next) return mentions;
  let start = 0;
  while (start < previous.length && start < next.length && previous[start] === next[start]) start++;
  let end = previous.length;
  let nextEnd = next.length;
  while (end > start && nextEnd > start && previous[end - 1] === next[nextEnd - 1]) { end--; nextEnd--; }

  // A known selection disambiguates repeated characters at mention boundaries.
  if (selection) {
    let selectedStart = selection.start;
    let selectedEnd = selection.end;
    const removed = previous.length - next.length;
    if (selectedStart === selectedEnd && removed > 0) {
      if (selection.inputType?.includes('Backward')) selectedStart -= removed;
      else if (selection.inputType?.includes('Forward')) selectedEnd += removed;
    }
    const insertedLength = next.length - previous.length + selectedEnd - selectedStart;
    if (selectedStart >= 0 && insertedLength >= 0
      && previous.slice(0, selectedStart) === next.slice(0, selectedStart)
      && previous.slice(selectedEnd) === next.slice(selectedStart + insertedLength)) {
      start = selectedStart; end = selectedEnd;
    }
  }

  const delta = next.length - previous.length;
  return mentions.flatMap((mention) => {
    if (end <= mention.start) return [{ ...mention, start: mention.start + delta, end: mention.end + delta }];
    if (start >= mention.end) return [mention];
    return [];
  }).filter((mention) => next.slice(mention.start, mention.end) === `@${mention.title}`);
}

export function insertChatMention(text: string, mentions: ChatMention[], query: MentionQuery, chat: MentionableChat) {
  if (mentions.length >= MAX_CHAT_MENTIONS || mentions.some((mention) => mention.chatId === chat.id)) return null;
  const selectedChat = normalizeMentionableChat(chat);
  const token = `@${selectedChat.title}`;
  const replacement = `${token} `;
  const next = text.slice(0, query.start) + replacement + text.slice(query.end);
  const shifted = reconcileMentionEdit(text, next, mentions, { start: query.start, end: query.end });
  const nextMentions = [...shifted, { chatId: selectedChat.id, title: selectedChat.title, start: query.start, end: query.start + token.length }].sort((a, b) => a.start - b.start);
  if (!validateChatMentions(nextMentions, next)) return null;
  return {
    text: next,
    mentions: nextMentions,
    caret: query.start + replacement.length
  };
}

export function trimMentionDraft(text: string, mentions: ChatMention[]) {
  const leading = Math.min(text.length - text.trimStart().length, mentions[0]?.start ?? text.length);
  const end = Math.max(text.trimEnd().length, mentions.at(-1)?.end ?? 0);
  return { text: text.slice(leading, end), mentions: mentions.map((mention) => ({ ...mention, start: mention.start - leading, end: mention.end - leading })) };
}
